import { api } from ".";
import {
  IMarketPriceDataset,
  IMarketPricesResponse,
  IMarketPricesQuery,
} from "../../types/marketPrice";
import {
  IMarketPriceAnalysisRequest,
  IMarketPriceAnalysisResponse,
} from "../../types/marketPriceAnalysis";

const marketPricesQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getMarketPriceDatasets: builder.query<
      IMarketPriceDataset[],
      { search?: string }
    >({
      query: (params) => ({
        url: "/market-prices/datasets",
        params,
      }),
      transformResponse: (response: { data: IMarketPriceDataset[] }) =>
        response.data,
      providesTags: ["MarketPrices"],
    }),
    getMarketPriceDatasetById: builder.query<IMarketPriceDataset, string>({
      query: (id) => `/market-prices/datasets/${id}`,
      transformResponse: (response: { data: IMarketPriceDataset }) =>
        response.data,
    }),
    uploadMarketPrices: builder.mutation<
      IMarketPriceDataset & { skippedCount: number },
      {
        file: File;
        displayName: string;
        description?: string;
        providerIds: string[];
      }
    >({
      query: ({ file, displayName, description, providerIds }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("displayName", displayName);
        if (description) {
          formData.append("description", description);
        }
        // Append each providerId separately for proper parsing
        providerIds.forEach((id) => {
          formData.append("providerIds", id);
        });

        return {
          url: "/market-prices/upload",
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (response: {
        data: IMarketPriceDataset;
        skippedCount: number;
      }) => ({ ...response.data, skippedCount: response.skippedCount }),
      invalidatesTags: ["MarketPrices"],
    }),
    updateMarketPriceDataset: builder.mutation<
      IMarketPriceDataset,
      {
        id: string;
        file?: File;
        displayName?: string;
        description?: string;
        providerIds?: string[];
      }
    >({
      query: ({ id, file, displayName, description, providerIds }) => {
        const formData = new FormData();

        if (file) {
          formData.append("file", file);
        }
        if (displayName !== undefined) {
          formData.append("displayName", displayName);
        }
        if (description !== undefined) {
          formData.append("description", description);
        }
        if (providerIds) {
          // Append each providerId separately for proper parsing
          providerIds.forEach((providerId) => {
            formData.append("providerIds", providerId);
          });
        }

        return {
          url: `/market-prices/datasets/${id}`,
          method: "PUT",
          body: formData,
        };
      },
      transformResponse: (response: { data: IMarketPriceDataset }) =>
        response.data,
      invalidatesTags: ["MarketPrices"],
    }),
    deleteMarketPriceDataset: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/market-prices/datasets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MarketPrices"],
    }),
    getMarketPriceDatasetDownloadUrl: builder.mutation<
      { url: string; message: string },
      string
    >({
      query: (id) => `/market-prices/datasets/${id}/download-url`,
    }),
    getMarketPrices: builder.query<
      IMarketPricesResponse,
      { datasetId: string } & IMarketPricesQuery
    >({
      query: ({ datasetId, ...params }) => ({
        url: `/market-prices/datasets/${datasetId}/prices`,
        params,
      }),
    }),
    getAllMarketPrices: builder.query<
      IMarketPricesResponse,
      IMarketPricesQuery
    >({
      query: (params) => ({
        url: `/market-prices/all`,
        params,
      }),
      providesTags: ["MarketPrices"],
    }),
    getMarketPriceAnalysis: builder.mutation<
      IMarketPriceAnalysisResponse,
      IMarketPriceAnalysisRequest
    >({
      query: (body) => ({
        url: `/market-prices/analysis`,
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: IMarketPriceAnalysisResponse }) =>
        response.data,
    }),
    getAvailableCompetitors: builder.query<
      string[],
      { payerId?: number; specialty?: string } | void
    >({
      query: (params) => ({
        url: `/market-prices/competitors`,
        params: params || {},
      }),
      transformResponse: (response: { data: string[] }) => response.data,
      providesTags: ["MarketPrices"],
    }),
    getPayersWithMarketData: builder.query<
      number[],
      { specialty?: string } | void
    >({
      query: (params) => ({
        url: `/market-prices/payers-with-data`,
        params: params || {},
      }),
      transformResponse: (response: { data: number[] }) => response.data,
      providesTags: ["MarketPrices"],
    }),
  }),
});

export const {
  useGetMarketPriceDatasetsQuery: useGetMarketPriceDatasets,
  useGetMarketPriceDatasetByIdQuery: useGetMarketPriceDatasetById,
  useUploadMarketPricesMutation: useUploadMarketPrices,
  useUpdateMarketPriceDatasetMutation: useUpdateMarketPriceDataset,
  useDeleteMarketPriceDatasetMutation: useDeleteMarketPriceDataset,
  useGetMarketPriceDatasetDownloadUrlMutation:
    useGetMarketPriceDatasetDownloadUrl,
  useGetMarketPricesQuery: useGetMarketPrices,
  useGetAllMarketPricesQuery: useGetAllMarketPrices,
  useGetMarketPriceAnalysisMutation: useGetMarketPriceAnalysis,
  useGetAvailableCompetitorsQuery: useGetAvailableCompetitors,
  useGetPayersWithMarketDataQuery: useGetPayersWithMarketData,
} = marketPricesQueries;
